/**
 * ClienteSelect — escolha de cliente por BUSCA, não por rolagem.
 *
 * V39 (auditoria 2026-08-07): a cobrança oferecia um `<select>` com a base
 * inteira dentro — 68 nomes já no teste, e a base real cresce. Pior: o
 * casing do cadastro é caótico ("denise", "ELBIA", "alexandre paixão" E
 * "ALEXANDRE PAIXÃO"), então a ordenação alfabética separa o mesmo nome em
 * pontos distantes da lista e o operador não acha quem procura — ou escolhe
 * o duplicado errado e a cobrança sai no cadastro errado.
 *
 * Aqui o operador digita e o servidor filtra (o mesmo `?busca=` que a tela
 * de clientes usa), então o tamanho da base deixa de importar.
 *
 * Nota de escopo: a DEDUPLICAÇÃO dos cadastros duplicados é outro problema
 * (decisão D9b — adiada, exige limpeza de dados com o dono). Este componente
 * ajuda a conviver com ela mostrando telefone/documento ao lado do nome,
 * que é como se distingue "Igor Ribeiro" de "Igor Ribeiro".
 */
import React, { useEffect, useRef, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";

import { listarClientes } from "../../services/comercial";

const MIN_BUSCA = 2;
const DEBOUNCE_MS = 350;

function subtitulo(cliente) {
  // O que distingue homônimos, na ordem em que a pessoa reconhece.
  return [cliente?.telefone, cliente?.documento, cliente?.cidade]
    .filter(Boolean)
    .join(" · ");
}

function ClienteSelect({
  request,
  value,
  onChange,
  disabled = false,
  placeholder = "Digite o nome, telefone ou documento…",
  autoFocus = false,
}) {
  const [termo, setTermo] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  // Busca no servidor com debounce — nunca baixa a base inteira.
  useEffect(() => {
    if (value) return undefined;
    const t = termo.trim();
    if (t.length < MIN_BUSCA) {
      setResultados([]);
      return undefined;
    }
    let ativo = true;
    setBuscando(true);
    const id = setTimeout(() => {
      listarClientes(request, { busca: t, pageSize: 10 })
        .then((p) => {
          if (!ativo) return;
          setResultados(Array.isArray(p) ? p : p?.results || []);
          setAberto(true);
        })
        .catch(() => ativo && setResultados([]))
        .finally(() => ativo && setBuscando(false));
    }, DEBOUNCE_MS);
    return () => {
      ativo = false;
      clearTimeout(id);
    };
  }, [termo, value, request]);

  // Clique fora fecha a lista (senão ela cobre os campos seguintes do form).
  useEffect(() => {
    if (!aberto) return undefined;
    const fechar = (ev) => {
      if (containerRef.current && !containerRef.current.contains(ev.target)) {
        setAberto(false);
      }
    };
    document.addEventListener("mousedown", fechar);
    return () => document.removeEventListener("mousedown", fechar);
  }, [aberto]);

  const escolher = (cliente) => {
    onChange?.(cliente);
    setTermo("");
    setResultados([]);
    setAberto(false);
  };

  if (value) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "8px 10px",
          border: "1px solid var(--border)",
          borderRadius: 6,
          background: "var(--surface-2, transparent)",
        }}
      >
        <span style={{ minWidth: 0 }}>
          <strong
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={value.nome}
          >
            {value.nome}
          </strong>
          {subtitulo(value) ? (
            <span style={{ fontSize: 11, opacity: 0.7 }}>{subtitulo(value)}</span>
          ) : null}
        </span>
        {disabled ? null : (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onChange?.(null)}
            aria-label="Trocar de cliente"
          >
            <FiX size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <FiSearch
          size={14}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.5,
            pointerEvents: "none",
          }}
        />
        <input
          type="text"
          value={termo}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(e) => setTermo(e.target.value)}
          onFocus={() => resultados.length > 0 && setAberto(true)}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "8px 10px 8px 30px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            background: "transparent",
            color: "inherit",
          }}
        />
      </div>

      {termo.trim().length > 0 && termo.trim().length < MIN_BUSCA ? (
        <span style={{ fontSize: 11, opacity: 0.65 }}>
          Digite pelo menos {MIN_BUSCA} caracteres.
        </span>
      ) : null}

      {aberto && (buscando || resultados.length > 0) ? (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid var(--border)",
            borderRadius: 6,
            background: "var(--surface)",
            boxShadow: "0 8px 24px rgba(0,0,0,.18)",
          }}
        >
          {buscando ? (
            <div style={{ padding: "8px 10px", fontSize: 12, opacity: 0.7 }}>
              Buscando…
            </div>
          ) : (
            resultados.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => escolher(c)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  border: "none",
                  borderBottom: "1px solid var(--border)",
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                }}
              >
                <span style={{ display: "block", fontWeight: 600 }}>{c.nome}</span>
                {subtitulo(c) ? (
                  <span style={{ fontSize: 11, opacity: 0.7 }}>{subtitulo(c)}</span>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}

      {aberto && !buscando && resultados.length === 0 && termo.trim().length >= MIN_BUSCA ? (
        <span style={{ fontSize: 11, opacity: 0.65 }}>
          Nenhum cliente encontrado para “{termo.trim()}”.
        </span>
      ) : null}
    </div>
  );
}

export default ClienteSelect;
